import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { useWebSocket } from "../../lib/utils/Websockets";
import moment from "moment";
import { ActivityIcon, MessageSquareIcon, PlayCircleIcon, ZapIcon, ShareIcon, CheckCircleIcon } from "lucide-react";
import { cn } from "../../lib/utils";

interface LiveEvent {
  id: string;
  type: "AGENT_INITIALIZED" | "AGENT_ACTION" | "AGENT_SUBAGENT_DELEGATION" | "AGENT_RESPONSE";
  session_id: string;
  agent_id: number;
  content?: string;
  details?: string;
  receivedAt: Date;
}

interface AgentLiveActivityProps {
  applicationId?: number;
  agentId: number;
}

const AgentLiveActivity: React.FC<AgentLiveActivityProps> = ({
  applicationId,
  agentId,
}) => {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const maxEvents = 5;
  
  // Parse incoming payload safely
  const parsePayload = (data: any) => {
    let payload = data.event_data || data.data || data;
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (e) { console.error("Parse error", e); return null; }
    }
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (e) { return null; }
    }
    return payload;
  };

  const handleAgentUpdate = (data: any) => {
    const payload = parsePayload(data);
    if (!payload) return;

    // Filter by agentId
    if (agentId && payload.agent_id && Number(payload.agent_id) !== Number(agentId)) {
        return;
    }

    const subtype = payload.subtype;
    let content = "";
    let details = "";

    switch (subtype) {
        case "AGENT_INITIALIZED":
            content = "Agent initialized";
            break;
        case "AGENT_ACTION":
            content = `Executing action: ${payload.action}`;
            details = payload.args ? JSON.stringify(payload.args).substring(0, 50) + "..." : "";
            break;
        case "AGENT_SUBAGENT_DELEGATION":
            content = `Delegating to ${payload.target_agent}`;
            break;
        case "AGENT_RESPONSE":
            content = "Response generated";
            const response = payload.response;
            details = response?.result?.response || response?.feedback || "";
            break;
        default:
            return; // Unknown subtype
    }

    const newEvent: LiveEvent = {
      id: `au-${Date.now()}-${Math.random()}`,
      type: subtype,
      session_id: payload.session_id || "unknown",
      agent_id: payload.agent_id,
      content: content,
      details: details,
      receivedAt: new Date(),
    };
    addEvent(newEvent);
  };

  const addEvent = (event: LiveEvent) => {
    setEvents((prev) => {
        // Simple dedupe
        if (prev.length > 0 && prev[0].session_id === event.session_id && prev[0].type === event.type && prev[0].content === event.content) {
            return prev;
        }
        return [event, ...prev].slice(0, maxEvents);
    });
  };

  useWebSocket({
    key: applicationId ? String(applicationId) : "",
    eventType: "AGENT_UPDATE",
    onMessage: handleAgentUpdate,
  });

  if (!applicationId) return null;

  return (
    <Card className="border-border h-full flex flex-col">
      <CardContent className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ActivityIcon className="w-5 h-5 text-maitai-green" />
            <h3 className="text-lg font-gc-demi-bold tracking-tight text-foreground">
              Live Feed
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-maitai-green opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-maitai-green"></span>
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-hidden relative">
          {events.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground bg-muted/10 rounded-lg border border-dashed border-border/50">
              <p className="text-sm font-gc-med">Waiting for activity...</p>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in duration-500">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="group flex gap-3 items-start relative pl-2"
                >
                  {/* Timeline connector - centered on icon (8px padding + 14px half-width = 22px) */}
                  <div className="absolute left-[22px] top-8 bottom-[-16px] w-[1px] bg-border last:hidden group-last:hidden" />
                  
                  <div className={cn(
                    "mt-1 w-7 h-7 rounded-full flex items-center justify-center shrink-0 border shadow-sm z-10",
                    event.type === "AGENT_INITIALIZED" 
                      ? "bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400"
                      : event.type === "AGENT_ACTION"
                      ? "bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-400"
                      : event.type === "AGENT_SUBAGENT_DELEGATION"
                      ? "bg-orange-50 border-orange-200 text-orange-600 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-400"
                      : "bg-green-50 border-green-200 text-green-600 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400"
                  )}>
                    {event.type === "AGENT_INITIALIZED" ? (
                      <PlayCircleIcon className="w-3.5 h-3.5" />
                    ) : event.type === "AGENT_ACTION" ? (
                      <ZapIcon className="w-3.5 h-3.5" />
                    ) : event.type === "AGENT_SUBAGENT_DELEGATION" ? (
                      <ShareIcon className="w-3.5 h-3.5" />
                    ) : (
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 py-0.5">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs font-mono text-muted-foreground">
                        {event.session_id.substring(0, 8)}
                      </span>
                      <span className="text-[10px] text-muted-foreground/50 whitespace-nowrap">
                        {moment(event.receivedAt).format("HH:mm:ss")}
                      </span>
                    </div>
                    
                    <p className="text-sm font-medium text-foreground">
                      {event.content}
                    </p>
                    {event.details && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {event.details}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentLiveActivity;
